import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft,
  ArrowRight,
  Shield,
  Users,
  Settings,
  Cpu,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Clock,
  XCircle,
  CalendarIcon,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const ActionDetailPage = () => {
  const { actionId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [action, setAction] = useState(null);
  const [decision, setDecision] = useState(null);
  const [reviewDate, setReviewDate] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const fetchAction = async () => {
      try {
        // First try to find action by risk_id (when coming from risks page)
        const actionsResponse = await api.get('/actions');
        const foundAction = actionsResponse.data.find(
          a => a.risk_id === actionId || a.id === actionId
        );
        
        if (foundAction) {
          setAction(foundAction);
          setDecision(foundAction.decision);
          setNotes(foundAction.notes || '');
          if (foundAction.review_date) {
            setReviewDate(new Date(foundAction.review_date));
          }
        } else {
          // Try direct action lookup as fallback
          const response = await api.get(`/actions/${actionId}`).catch(() => null);
          if (response?.data) {
            setAction(response.data);
            setDecision(response.data.decision);
            setNotes(response.data.notes || '');
            if (response.data.review_date) {
              setReviewDate(new Date(response.data.review_date));
            }
          }
        }
      } catch (error) {
        toast.error('Uw gegevens konden niet worden geladen. Ververs de pagina en probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchAction();
  }, [api, actionId]);

  const handleSubmit = async (selectedDecision) => {
    if (selectedDecision === 'postpone' && !reviewDate) {
      toast.error('Selecteer een review datum voor uitstel');
      return;
    }

    setSubmitting(true);

    try {
      await api.patch(`/actions/${action.id}/decision`, {
        decision: selectedDecision,
        review_date: reviewDate?.toISOString(),
        notes: notes || null,
      });
      
      toast.success(
        selectedDecision === 'do' ? 'Actie gemarkeerd als uitgevoerd!' :
        selectedDecision === 'postpone' ? 'Actie uitgesteld' :
        'Actie overgeslagen'
      );
      
      navigate('/dashboard');
    } catch (error) {
      const message = getUserErrorMessage(error, 'De beslissing kon niet worden opgeslagen. Probeer het later opnieuw.');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'people':
        return <Users className="h-5 w-5" strokeWidth={1.5} />;
      case 'process':
        return <Settings className="h-5 w-5" strokeWidth={1.5} />;
      case 'technology':
        return <Cpu className="h-5 w-5" strokeWidth={1.5} />;
      default:
        return <Shield className="h-5 w-5" strokeWidth={1.5} />;
    }
  };

  const getRiskLevelBadge = (level) => {
    const variants = {
      high: 'border-transparent bg-red-50 text-red-700',
      medium: 'border-transparent bg-orange-50 text-orange-700',
      low: 'border-transparent bg-green-50 text-green-700',
      unknown: 'border-transparent bg-neutral-100 text-neutral-600',
    };
    const labels = {
      high: 'Hoog risico',
      medium: 'Middel risico',
      low: 'Laag risico',
      unknown: 'Onbekend',
    };
    return (
      <Badge className={variants[level] || variants.unknown}>
        {labels[level] || 'Onbekend'}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </MainLayout>
    );
  }

  if (!action) {
    return (
      <MainLayout>
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center py-12">
            <AlertTriangle className="h-12 w-12 text-neutral-400 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-semibold text-black mb-2">Actie niet gevonden</h2>
            <p className="text-neutral-600 mb-6">Deze actie bestaat niet of is al afgerond.</p>
            <Link to="/dashboard">
              <Button className="bg-black text-white hover:bg-neutral-800">
                <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Terug naar dashboard
              </Button>
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 lg:p-8 space-y-8 max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/risks"
          className="inline-flex items-center text-sm text-neutral-500 hover:text-black transition-colors"
          data-testid="back-to-risks-link"
        >
          <ArrowLeft className="mr-1 h-4 w-4" strokeWidth={1.5} />
          Terug naar risico's
        </Link>

        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-neutral-100 flex items-center justify-center">
              {getTypeIcon(action.risk_type)}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                {action.risk_type === 'people' ? 'Mensen' : action.risk_type === 'process' ? 'Processen' : 'Technologie'}
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-black">{action.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getRiskLevelBadge(action.risk_level)}
            <span className="text-sm text-neutral-500">Prioriteit {action.priority}</span>
          </div>
        </div>

        {/* Description Card */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
              Beschrijving
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-neutral-700 leading-relaxed">{action.description}</p>
          </CardContent>
        </Card>

        {/* Cost Analysis Card */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="cost-analysis-card">
          <CardHeader>
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
              Kosten-baten analyse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Cost of Inaction */}
              <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                  <p className="text-sm font-medium text-red-700">Kosten bij niet handelen</p>
                </div>
                <p className="text-3xl font-semibold text-red-600" data-testid="cost-of-inaction">
                  {formatCurrency(action.cost_of_inaction || 0)}
                </p>
                <p className="text-xs text-red-600 mt-1">Geschat financieel risico per jaar</p>
              </div>

              {/* Risk Reduction */}
              <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                  <p className="text-sm font-medium text-green-700">Verwachte risicoreductie</p>
                </div>
                <p className="text-3xl font-semibold text-green-600" data-testid="risk-reduction">
                  {action.estimated_risk_reduction || 0}%
                </p>
                <p className="text-xs text-green-600 mt-1">Vermindering na implementatie maatregel</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Decision Card */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="decision-card">
          <CardHeader>
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
              Neem een beslissing
            </CardTitle>
            <p className="text-xs text-neutral-500 mt-1">Kies wat u met dit actiepunt wilt doen</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Decision Buttons — prominent cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => !submitting && handleSubmit('do')}
                disabled={submitting}
                className={`p-4 rounded-xl border-2 text-left transition-all ${decision === 'do' ? 'border-green-500 bg-green-50 ring-2 ring-green-200' : 'border-neutral-200 hover:border-green-300 hover:bg-green-50/50'}`}
                data-testid="decision-do-button"
              >
                <CheckCircle className="h-6 w-6 text-green-600 mb-2" strokeWidth={1.5} />
                <p className="text-sm font-bold text-black">Uitvoeren</p>
                <p className="text-[10px] text-neutral-500 mt-0.5">Markeer als actie en ga aan de slag</p>
              </button>
              <button
                onClick={() => !submitting && reviewDate && handleSubmit('postpone')}
                disabled={submitting || !reviewDate}
                className={`p-4 rounded-xl border-2 text-left transition-all ${decision === 'postpone' ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200' : 'border-neutral-200 hover:border-amber-300 hover:bg-amber-50/50'} ${!reviewDate ? 'opacity-60' : ''}`}
                data-testid="decision-postpone-button"
              >
                <Clock className="h-6 w-6 text-amber-600 mb-2" strokeWidth={1.5} />
                <p className="text-sm font-bold text-black">Uitstellen</p>
                <p className="text-[10px] text-neutral-500 mt-0.5">{reviewDate ? `Review op ${format(reviewDate, 'dd-MM-yyyy')}` : 'Selecteer eerst een review datum'}</p>
              </button>
              <button
                onClick={() => !submitting && handleSubmit('skip')}
                disabled={submitting}
                className={`p-4 rounded-xl border-2 text-left transition-all ${decision === 'skip' ? 'border-neutral-500 bg-neutral-50 ring-2 ring-neutral-200' : 'border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50'}`}
                data-testid="decision-skip-button"
              >
                <XCircle className="h-6 w-6 text-neutral-400 mb-2" strokeWidth={1.5} />
                <p className="text-sm font-bold text-black">Niet uitvoeren</p>
                <p className="text-[10px] text-neutral-500 mt-0.5">Accepteer het risico bewust</p>
              </button>
            </div>

            {/* Review Date for Postpone */}
            <div className="space-y-2 p-4 rounded-xl bg-neutral-50 border border-neutral-100">
              <Label className="text-xs font-medium text-neutral-600">
                Review datum (vereist bij uitstel)
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal border-neutral-200 bg-white"
                    data-testid="review-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    {reviewDate ? format(reviewDate, 'PPP', { locale: nl }) : 'Selecteer een datum'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={reviewDate}
                    onSelect={setReviewDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-xs font-medium text-neutral-600">
                Notities (optioneel)
              </Label>
              <Textarea
                id="notes"
                placeholder="Voeg een toelichting toe bij uw beslissing..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="border-neutral-200 focus:border-black focus:ring-black min-h-[80px]"
                data-testid="notes-textarea"
              />
            </div>

            {submitting && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                <span className="text-xs text-neutral-500 ml-2">Beslissing opslaan...</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leverancier CTA */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] bg-neutral-50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-black">Hulp nodig bij implementatie?</h3>
                <p className="text-sm text-neutral-600">Vind een geverifieerde leverancier die dit voor u kan uitvoeren.</p>
              </div>
              <Link to="/leveranciers">
                <Button variant="outline" className="border-neutral-200 bg-white" data-testid="find-leverancier-button">
                  Vind leverancier
                  <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ActionDetailPage;
